import ApexCharts from "apexcharts";
import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy, ViewChild, ElementRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FindingDetailsModalService } from "src/app/services/performance-service/finding-details-modal.service";
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: "app-cre-modal",
  standalone: true,
  imports: [
    TranslateModule,CommonModule],
  templateUrl: "./cre-modal.component.html",
  styleUrls: ["./cre-modal.component.scss"],
})
export class CREModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();

  constructor(private findingDetailsModalService: FindingDetailsModalService) {}

  @ViewChild("ccChartEl") ccChartEl!: ElementRef<HTMLDivElement>;
  @ViewChild("creChartEl") creChartEl!: ElementRef<HTMLDivElement>;

  private ccChart: ApexCharts | null = null;
  private creChart: ApexCharts | null = null;

  /** Full rows from cre_table.data.creTableData — set by the parent via setCreTableData(). */
  creTableData: any[] = [];

  setCreTableData(rows: any[]): void {
    this.creTableData = rows ?? [];
    this.overdueFindings = this.creTableData.map(row => {
      const due = new Date(row.findings_due_date);
      const isOverdue = due < new Date();
      const mm = String(due.getMonth() + 1).padStart(2, "0");
      return {
        date:          `${mm}/${due.getFullYear()}`,
        location:      row.location ?? "",
        level:         parseInt((row.finding_type ?? "").replace(/\D/g, ""), 10) || 0,
        dateColor:     isOverdue ? "#f00014" : "#009146",
        findingNumber: row.finding_number,
      };
    });
  }

  openFindingDetails(row: { location: string; findingNumber?: string }): void {
    const match = this.creTableData.find(
      r => r.finding_number === (row as any).findingNumber || r.location === row.location
    );
    if (match) {
      this.findingDetailsModalService.openWithCreRow(match);
    } else {
      this.findingDetailsModalService.open();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["isOpen"]) {
      const ionContent = document.querySelector("ion-content.dashboard-wrapper") as HTMLElement;
      if (ionContent) {
        if (this.isOpen) {
          ionContent.style.setProperty("--overflow", "hidden");
        } else {
          ionContent.style.removeProperty("--overflow");
        }
      }
      if (this.isOpen) {
        setTimeout(() => this.buildCharts(), 0);
      } else {
        this.destroyCharts();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyCharts();
  }

  close() {
    this.closeModal.emit();
  }

  private buildCharts(): void {
    this.destroyCharts();

    if (this.ccChartEl?.nativeElement) {
      this.ccChart = new ApexCharts(
        this.ccChartEl.nativeElement,
        this.sparklineOptions(this.ccChartData, this.lineColor(this.ccScore))
      );
      this.ccChart.render();
    }

    if (this.creChartEl?.nativeElement) {
      this.creChart = new ApexCharts(
        this.creChartEl.nativeElement,
        this.sparklineOptions(this.creChartData, this.lineColor(this.creScore))
      );
      this.creChart.render();
    }
  }

  private destroyCharts(): void {
    this.ccChart?.destroy();
    this.ccChart = null;
    this.creChart?.destroy();
    this.creChart = null;
  }

  private sparklineOptions(data: number[], color: string): any {
    const max = data.length ? Math.max(...data) : 100;
    const lastIndex = data.length - 1;
    return {
      series: [{ name: "", data }],
      chart: {
        type: "line",
        height: 64,
        width: "100%",
        background: "transparent",
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
        sparkline: { enabled: true },
      },
      stroke: { curve: "smooth", width: 1.5, colors: [color] },
      xaxis: {
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { show: false, min: 0, max: max * 1.45 },
      grid: { show: false, padding: { left: 0, right: 0, top: 8, bottom: 6 } },
      markers: {
        size: 2.5,
        colors: [color],
        strokeWidth: 0,
        discrete: [
          { seriesIndex: 0, dataPointIndex: 0,         fillColor: "transparent", strokeColor: "transparent", size: 0 },
          { seriesIndex: 0, dataPointIndex: lastIndex, fillColor: "transparent", strokeColor: "transparent", size: 0 },
        ],
      },
      legend: { show: false },
      dataLabels: { enabled: false },
      tooltip: { enabled: false },
      colors: [color],
    };
  }

  lineColor(score: number): string {
    if (score < 55)   return "#9F0014";
    if (score < 85)   return "#FFDE72";
    if (score < 95)   return "#94C12E";
    if (score <= 100) return "#009146";
    return "#800080";
  }

  chartBg(score: number): string {
    if (score < 55)   return 'linear-gradient(166deg, rgb(245,229,231) 3%, rgb(245,229,231) 44%, rgb(236,206,209) 63%, rgb(236,206,209) 84%)';
    if (score < 85)   return 'linear-gradient(166deg, rgba(255,249,229,1) 3%, rgba(255,249,229,1) 44%, rgba(255,244,206,1) 63%, rgba(255,244,206,1) 84%)';
    if (score < 95)   return 'linear-gradient(166deg, rgba(244,249,234,1) 3%, rgba(244,249,234,1) 44%, rgba(234,243,215,1) 63%, rgba(234,243,215,1) 84%)';
    if (score <= 100) return 'linear-gradient(166deg, rgba(229,244,236,1) 3%, rgba(229,244,236,1) 44%, rgba(206,234,219,1) 63%, rgba(206,234,219,1) 84%)';
    return 'linear-gradient(166deg, rgba(239,229,245,1) 3%, rgba(239,229,245,1) 44%, rgba(225,206,236,1) 63%, rgba(225,206,236,1) 84%)';
  }

  // ── Header ────────────────────────────────────────────────────────────
  headerIconSrc = "assets/performance/cre-shield.svg";
  headerTitle   = "Critical Risks Elimination";

  // ── AI Box ────────────────────────────────────────────────────────────
  aiText = "AI Generated- Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud";

  // ── Chart Scores ──────────────────────────────────────────────────────
  ccScore  = 92;
  creScore = 45;

  // ── Chart Data ────────────────────────────────────────────────────────
  ccChartData  = [65, 72, 78, 82, 88, 91, 92];
  creChartData = [80, 75, 65, 58, 52, 47, 45];

  // ── Critical Controls Card ────────────────────────────────────────────
  ccCard = {
    iconSrc: "assets/performance/critical-control.svg",
    iconAlt: "CC",
    title:   "Critical Controls",
    subtitle: "Compliance (Q4 2024)",
  };

  ccTop3    = [{ name: "Unit A", score: "100%" }, { name: "Unit B", score: "98%" }, { name: "Unit C", score: "94%" }];
  ccBottom3 = [{ name: "Unit D", score: "66%" },  { name: "Unit E", score: "71%" }, { name: "Unit F", score: "71%" }];
  ccVerification = "Verification completed: 100%";

  // ── CRE Card ──────────────────────────────────────────────────────────
  creCard = {
    iconSrc:  "assets/performance/cre-shield.svg",
    iconAlt:  "CRE",
    title:    "Critical Risk Elimination",
    subtitle: "On-time finding closure",
  };

  // ── CRE Progress Bar ──────────────────────────────────────────────────
  creProgress = { closed: 61.7, open: 34.3, overdue: 4 };
  progressLegend = [
    { label: "Closed", colorClass: "cm-progress-legend__dot--green" },
    { label: "Open",   colorClass: "cm-progress-legend__dot--yellow" },
    { label: "Overdue", colorClass: "cm-progress-legend__dot--red" },
  ];

  // ── CRE Stats ─────────────────────────────────────────────────────────
  creStats = [
    { value: 768, label: "Closed this\nyear" },
    { value: 95,  label: "Due this\nquarter" },
    { value: 536, label: "Due this\nyear" },
  ];

  // ── Overdue CRE Findings ──────────────────────────────────────────────
  overdueTitle    = "Overdue CRE Findings:";
  overdueSubtitle = "Impacting country CIS";
  overdueColumns  = ["Date", "Location", "Level"];
  searchIconSrc   = "assets/performance/search-icon.svg";

  overdueFindings: Array<{ date: string; location: string; level: number; dateColor: string; findingNumber?: string }> = [];
}
